import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DriveFolder } from '../services/googleDriveService';
import {
  DefaultDriveFolder,
  getDefaultDriveFolder,
  setDefaultDriveFolder,
} from '../utils/userPreferences';
import DriveFolderPickerModal from './DriveFolderPickerModal';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [defaultFolder, setDefaultFolder] = useState<DefaultDriveFolder | null>(null);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDefaultFolder(getDefaultDriveFolder(user?.id));
    setSavedMessage(null);
    setShowFolderPicker(false);
  }, [isOpen, user?.id]);

  if (!isOpen) return null;

  const handleFolderSelected = (folder: DriveFolder) => {
    setDefaultDriveFolder(user?.id, folder);
    setDefaultFolder(folder);
    setShowFolderPicker(false);
    setSavedMessage('Default save folder updated.');
  };

  const handleClearFolder = () => {
    setDefaultDriveFolder(user?.id, null);
    setDefaultFolder(null);
    setSavedMessage('Default save folder cleared. You will pick a folder each time you save.');
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full h-full sm:h-auto sm:max-w-lg sm:mx-4 bg-white dark:bg-slate-800 sm:rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-6 py-5" style={{ backgroundColor: '#15452f' }}>
            <h2 className="text-2xl font-bold text-white">Settings</h2>
            <p className="text-emerald-100 mt-1">Preferences for saving prepared documents</p>
          </div>

          <div className="p-6 space-y-5">
            <section className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Google Drive
              </h3>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Default Save Folder</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Files are saved into a student-code subfolder inside this folder (e.g.{' '}
                    <span className="font-mono text-xs">My Caseload / LMS-04-01 /</span>).
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200">
                  {defaultFolder ? (
                    <span>
                      Current: <span className="font-semibold">{defaultFolder.name}</span>
                    </span>
                  ) : (
                    <span className="text-slate-500 dark:text-slate-400">No default folder set</span>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => setShowFolderPicker(true)}
                    className="flex-1 py-2.5 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                  >
                    {defaultFolder ? 'Change folder' : 'Choose folder'}
                  </button>
                  {defaultFolder && (
                    <button
                      type="button"
                      onClick={handleClearFolder}
                      className="py-2.5 px-4 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </section>

            {savedMessage && (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">{savedMessage}</p>
            )}
          </div>

          <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            >
              Close
            </button>
          </div>

          <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <DriveFolderPickerModal
        isOpen={showFolderPicker}
        title="Choose default save folder"
        onClose={() => setShowFolderPicker(false)}
        onSelect={handleFolderSelected}
      />
    </>
  );
};

export default SettingsModal;
